import { LaneData, MetricData, NodeData } from './types';

const doneStatuses = new Set(['Approved', 'Complete']);

function percent(value: number, total: number) {
  if (total === 0) {
    return '0%';
  }

  return `${Math.round((value / total) * 100)}%`;
}

function daysSince(dateValue: string) {
  const value = new Date(dateValue).getTime();

  if (Number.isNaN(value)) {
    return 0;
  }

  return Math.floor((Date.now() - value) / 86_400_000);
}

export function computeLaneCompletion(lane: LaneData, nodes: NodeData[]) {
  const laneNodes = nodes.filter((node) => node.lane === lane.id && !node.archivedAt);

  if (laneNodes.length === 0) {
    return lane.completion || 0;
  }

  const done = laneNodes.filter((node) => doneStatuses.has(node.status)).length;
  return Math.round((done / laneNodes.length) * 100);
}

export function countAlerts(nodes: NodeData[]) {
  const deprecatedDependencyCount = nodes.reduce(
    (total, node) =>
      total +
      node.dependencies.filter((dependency) => dependency.status === 'Deprecated').length,
    0
  );
  const highPriorityDrafts = nodes.filter(
    (node) => node.priority === 'High' && ['Draft', 'In Review'].includes(node.status)
  ).length;
  const staleReviewItems = nodes.filter(
    (node) => node.status === 'In Review' && daysSince(node.updatedAt) > 14
  ).length;

  return deprecatedDependencyCount + highPriorityDrafts + staleReviewItems;
}

export function computeMetrics(nodes: NodeData[], lanes: LaneData[]): MetricData[] {
  const activeNodes = nodes.filter((node) => !node.archivedAt);
  const screenCount = activeNodes.reduce(
    (total, node) => total + (node.childScreens.length || node.screenCount),
    0
  );
  const workflowCount = activeNodes.reduce((total, node) => {
    const nodeIsWorkflow =
      node.category === 'Workflow' || node.title.toLowerCase().includes('workflow');
    const dependencyWorkflows = node.dependencies.filter((dependency) =>
      dependency.label.toLowerCase().includes('workflow')
    ).length;
    return total + (nodeIsWorkflow ? 1 : 0) + dependencyWorkflows;
  }, 0);
  const roleCount = new Set(activeNodes.map((node) => node.owner).filter(Boolean)).size;
  const integrationLabels = new Set<string>();

  activeNodes.forEach((node) => {
    if (node.category === 'Integration') {
      integrationLabels.add(node.title);
    }

    node.dependencies.forEach((dependency) => {
      if (dependency.label.toLowerCase().includes('integration')) {
        integrationLabels.add(dependency.label);
      }
    });
  });

  const doneCount = activeNodes.filter((node) => doneStatuses.has(node.status)).length;
  const alertCount = countAlerts(activeNodes);
  const laneCompletion = lanes.map((lane) => computeLaneCompletion(lane, activeNodes));
  const averageLaneCompletion =
    laneCompletion.length > 0
      ? Math.round(laneCompletion.reduce((total, value) => total + value, 0) / laneCompletion.length)
      : 0;

  return [
    {
      id: 'metric-sections',
      label: 'Sections',
      value: String(activeNodes.length).padStart(2, '0'),
      sub: `${activeNodes.filter((node) => daysSince(node.createdAt) <= 7).length} new this week`,
      color: '#0F9B8E',
      trend: 'up',
    },
    {
      id: 'metric-screens',
      label: 'Screens',
      value: String(screenCount).padStart(2, '0'),
      sub: `${activeNodes.filter((node) => node.status === 'In Review').length} in review`,
      color: '#3B82F6',
      trend: 'up',
    },
    {
      id: 'metric-workflows',
      label: 'Workflows',
      value: String(workflowCount).padStart(2, '0'),
      sub: `${activeNodes.filter((node) => node.dependencies.length === 0).length} unlinked`,
      color: '#F59E0B',
      trend: workflowCount > 0 ? 'warn' : 'ok',
    },
    {
      id: 'metric-roles',
      label: 'Roles',
      value: String(roleCount).padStart(2, '0'),
      sub: 'Owners assigned',
      color: '#22C55E',
      trend: 'ok',
    },
    {
      id: 'metric-integrations',
      label: 'Integrations',
      value: String(integrationLabels.size).padStart(2, '0'),
      sub: `${activeNodes.filter((node) => node.status === 'Deprecated').length} deprecated`,
      color: '#A78BFA',
      trend: activeNodes.some((node) => node.status === 'Deprecated') ? 'warn' : 'ok',
    },
    {
      id: 'metric-complete',
      label: 'Complete',
      value: percent(doneCount, activeNodes.length),
      sub: `${doneCount} of ${activeNodes.length} sections`,
      color: '#22C55E',
      trend: averageLaneCompletion >= 60 ? 'ok' : 'warn',
    },
    {
      id: 'metric-alerts',
      label: 'Alerts',
      value: String(alertCount).padStart(2, '0'),
      sub: alertCount === 1 ? 'Needs attention' : 'Need attention',
      color: '#F87171',
      trend: alertCount > 0 ? 'error' : 'ok',
    },
  ];
}
